import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { productPacks } from '@/mocks/products';

const layers = [
  { name: '用户层', desc: '政府、产业方、运营方、商户、消费者等角色', href: '/jiuchou/architecture' },
  { name: '业务场景层', desc: '数字商业、供应链、消费与权益', href: '/jiuchou/architecture' },
  { name: '交易权益层', desc: '交易、权益与消费引擎', href: '/jiuchou/architecture' },
  { name: '数字基础设施层', desc: '数字货架、ePin、IoT、数据看板', href: '/jiuchou/architecture' },
  { name: '平台治理层', desc: '清分结算、合规与治理', href: '/jiuchou/architecture' },
];

const jcModules = [
  { name: '一条业务主链', desc: '一笔交易，一条数据链', href: '/jiuchou/chain' },
  { name: '数字商业节点', desc: '云社员闭环 · 云店数字经营', href: '/jiuchou/yun-node' },
  { name: '供应链与服务', desc: '农产品数字化上行 · 服务经济', href: '/jiuchou/supply-service' },
  { name: '消费与权益', desc: '聚餐场景 · 权益系统消费引擎', href: '/jiuchou/consumption' },
  { name: '透明清分账本', desc: '七方共利价值分配', href: '/jiuchou/settlement' },
  { name: '数据飞轮与 AI', desc: '数据闭环 · TOMA AI · AI 应用服务商', href: '/jiuchou/data-ai' },
];

export default function ProductsServices() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <SEO
        title="产品服务 | 九畴云市产品与技术服务能力"
        description="华辉数智通过 AI 应用、数字商业、IoT 连接和系统集成，为县域与乡村场景提供从研发到部署运维的完整技术服务。"
        keywords="九畴云市,产品服务,五层平台架构,TOMA智能体,数字货架,系统集成"
        path="/products-services"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '产品服务',
          description: '九畴云市产品与技术服务能力',
        }}
      />
      <PageHero
        eyebrow="产品与解决方案 · 产品服务"
        title="产品服务"
        titleHighlight="九畴云市产品与技术服务"
        subtitle="华辉数智通过 AI 应用、数字商业、IoT 连接和系统集成，为县域与乡村场景提供从研发到部署运维的完整技术服务。"
        desc="从五层平台架构到一条业务主链，再到产品矩阵，构成九畴云市可复制、可运营的数字商业能力底座。"
        imageUrl="https://readdy.ai/api/search-image?query=Abstract%20digital%20commerce%20platform%20visualization%20with%20layered%20technology%20stack%2C%20data%20dashboards%2C%20digital%20shelves%20and%20connected%20network%20nodes%2C%20deep%20graphite%20background%20with%20green%20and%20gold%20accent%20glows%2C%20futuristic%20enterprise%20software%20aesthetic%2C%20clean%20minimal%20composition%2C%20cinematic%20lighting&width=1600&height=900&seq=products-services-hero-01&orientation=landscape"
        imageAlt="九畴云市产品与技术服务"
      />
      <Breadcrumb items={[{ label: '产品与解决方案' }, { label: '产品服务' }]} />

      <main>
        {/* 九畴云市总览 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <SectionHeading
              eyebrow="九畴云市总览"
              title="乡村数字经济操作系统"
              subtitle="九畴云市是华辉数智的核心战略项目，以真实业务链为核心，连接人、货、店、场、数据与消费。"
            />
            <Reveal className="mt-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { icon: 'ri-robot-2-line', title: 'AI 应用', desc: 'TOMA 智能体作为 AI 交互入口' },
                  { icon: 'ri-store-2-line', title: '数字商业', desc: '数字货架 / 云铺系统承载交易' },
                  { icon: 'ri-router-line', title: 'IoT 连接', desc: '车载卡 / 智联设备物联协同' },
                  { icon: 'ri-dashboard-3-line', title: '系统集成', desc: '平台 / 接口 / 数据看板' },
                  { icon: 'ri-flow-chart', title: '智能清分', desc: '多方协同自动结算' },
                  { icon: 'ri-database-2-line', title: '数据飞轮', desc: '数据沉淀为可运营资产' },
                ].map((item) => (
                  <div key={item.title} className="p-5 rounded-lg bg-background-100 border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <div className="mt-3 font-heading font-bold text-foreground-950">{item.title}</div>
                    <p className="mt-1 text-sm text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* 五层平台架构 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <SectionHeading
              eyebrow="五层平台架构"
              title="从用户层到治理层"
              subtitle="五层架构支撑九畴云市从用户角色到平台治理的完整技术实现。"
            />
            <Reveal className="mt-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {layers.map((layer, idx) => (
                  <Link
                    key={layer.name}
                    to={layer.href}
                    className="p-5 rounded-lg bg-background-50 border border-background-200 cursor-pointer transition-colors hover:border-primary-300 group"
                  >
                    <div className="text-xs font-semibold text-primary-500">0{idx + 1}</div>
                    <div className="mt-2 font-heading font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {layer.name}
                    </div>
                    <p className="mt-1 text-xs text-foreground-600 leading-relaxed">{layer.desc}</p>
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* 业务模块 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <SectionHeading
              eyebrow="业务模块"
              title="一条主链 · 六个模块"
              subtitle="从业务主链到数字商业节点、供应链、消费、清分与数据 AI，构成完整的产品能力。"
            />
            <Reveal className="mt-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {jcModules.map((mod) => (
                  <Link
                    key={mod.name}
                    to={mod.href}
                    className="p-5 rounded-lg bg-background-100 border border-background-200 cursor-pointer transition-colors hover:border-primary-300 group flex items-start gap-3"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0">
                      <i className="ri-arrow-right-up-line"></i>
                    </div>
                    <div>
                      <div className="font-heading font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {mod.name}
                      </div>
                      <p className="mt-1 text-sm text-foreground-600">{mod.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* 产品矩阵 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <SectionHeading
              eyebrow="产品矩阵"
              title="小快轻准产品服务矩阵"
              subtitle="四类标准化产品包，统一采用「产品名称—解决的问题—核心功能—适用对象—交付方式—业务价值」结构。"
            />
            <Reveal className="mt-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {productPacks.map((pack) => (
                  <div key={pack.code} className="p-6 rounded-lg bg-background-50 border border-background-200">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                        <i className={`${pack.icon} text-xl`}></i>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-primary-500">{pack.code}</div>
                        <div className="font-heading font-bold text-foreground-950">{pack.name}</div>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                      <span className="font-semibold text-foreground-900">解决的问题：</span>
                      {pack.problem}
                    </p>
                    <div className="mt-3 text-sm text-foreground-600">
                      <span className="font-semibold text-foreground-900">核心功能：</span>
                      {pack.features.join('、')}
                    </div>
                    <div className="mt-3 text-sm text-foreground-600">
                      <span className="font-semibold text-foreground-900">适用对象：</span>
                      {pack.target.join('、')}
                    </div>
                    <div className="mt-3 text-sm text-foreground-600">
                      <span className="font-semibold text-foreground-900">交付方式：</span>
                      {pack.deploy.join(' → ')}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}